import type { Country } from '@/components/ui/country-dropdown';
import countriesJson from '@/utils/countries.json';
import { SHIPPING_ALL_VALUE } from '@/@types/ShippingConfigurations';

type CountryWithCurrencies = Country & { currencies?: string[] };

export type ShippingRegion = CountryWithCurrencies & {
  type: 'region';
  countries: string[];
  regionKind: string;
  regionType: string;
};

export type CountryComboOption = {
  value: string;
  label: string;
  countryCode?: string;
  isRegion?: boolean;
};

function getRegionDisplayNames() {
  try {
    return new Intl.DisplayNames(['pt-BR'], { type: 'region' });
  } catch {
    return null;
  }
}

function getCountryNameParts(rawName: string) {
  const parts = String(rawName || '')
    .split('|')
    .map((s) => s.trim())
    .filter(Boolean);
  return {
    primary: parts[0] ?? rawName,
    secondary: parts[1] ?? '',
  };
}

function getDisplayName(
  country: Country,
  regionDisplayNames: Intl.DisplayNames | null,
) {
  const ptName =
    regionDisplayNames?.of(country.alpha2.toUpperCase()) ?? '';
  const { primary, secondary } = getCountryNameParts(country.name);

  if (primary && ptName) return `${primary} | ${ptName}`;
  return secondary ? `${primary} | ${secondary}` : primary || country.name;
}

function isShippingRegion(
  entry: CountryWithCurrencies,
): entry is ShippingRegion {
  return entry.type === 'region' && Array.isArray(entry.countries);
}

function isAvailableCountry(country: CountryWithCurrencies) {
  return (
    !isShippingRegion(country) &&
    country.emoji &&
    country.status !== 'deleted' &&
    country.ioc !== 'PRK'
  );
}

const jsonSource = countriesJson as CountryWithCurrencies[];

const shippingRegions = jsonSource.filter(isShippingRegion);

const regionByCode = new Map(
  shippingRegions.map((region) => [region.alpha2.toUpperCase(), region]),
);

const countrySource = jsonSource.filter(isAvailableCountry);

const alpha2ToCurrenciesMap = new Map<string, string[]>(
  countrySource.map((country) => [
    country.alpha2.toUpperCase(),
    (country.currencies ?? []).map((currency) => currency.toUpperCase()),
  ]),
);

const availableCountries = countrySource
  .map((country) => {
    const regionDisplayNames = getRegionDisplayNames();
    const label = getDisplayName(country, regionDisplayNames);

    return {
      alpha2: country.alpha2.toUpperCase(),
      alpha3: String((country as Country & { alpha3?: string }).alpha3 ?? '')
        .toUpperCase()
        .trim(),
      label,
      searchValue: [
        label,
        country.name,
        country.alpha2,
        country.alpha3,
        regionDisplayNames?.of(country.alpha2.toUpperCase()) ?? '',
        ...(country.countryCallingCodes ?? []),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, ''),
    };
  });

const alpha2ToAlpha3Map = new Map(
  availableCountries
    .filter((c) => c.alpha2 && c.alpha3)
    .map((c) => [c.alpha2, c.alpha3]),
);

const alpha3ToAlpha2Map = new Map(
  availableCountries
    .filter((c) => c.alpha2 && c.alpha3)
    .map((c) => [c.alpha3, c.alpha2]),
);

export function alpha2ToAlpha3(code: string): string {
  const v = String(code || '').toUpperCase().trim();
  return alpha2ToAlpha3Map.get(v) ?? v;
}

export function alpha3ToAlpha2(code: string): string {
  const v = String(code || '').toUpperCase().trim();
  return alpha3ToAlpha2Map.get(v) ?? v;
}

export function normalizeCountryToAlpha2(code: string): string {
  const v = String(code || '').toUpperCase().trim();
  if (v.length === 3) return alpha3ToAlpha2(v);
  return v;
}

const availableRegionOptions = shippingRegions.map((region) => ({
  alpha2: region.alpha2.toUpperCase(),
  label: region.name,
  searchValue: region.name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, ''),
}));

function toComboOption(
  country: (typeof availableCountries)[number],
): CountryComboOption {
  return {
    value: country.alpha2,
    label: country.label,
    countryCode: country.alpha2,
  };
}

function toRegionComboOption(
  region: (typeof availableRegionOptions)[number],
): CountryComboOption {
  return {
    value: region.alpha2,
    label: region.label,
    isRegion: true,
  };
}

export function isShippingRegionCode(code: string): boolean {
  return regionByCode.has(String(code || '').toUpperCase().trim());
}

export function expandShippingRegionsToCountryCodes(
  codes: string[],
): string[] {
  const expanded = new Set<string>();

  for (const code of codes) {
    const upper = String(code || '').toUpperCase().trim();
    if (!upper || upper === SHIPPING_ALL_VALUE) continue;

    const region = regionByCode.get(upper);
    if (region) {
      region.countries.forEach((countryCode) =>
        expanded.add(countryCode.toUpperCase()),
      );
      continue;
    }

    expanded.add(upper);
  }

  return Array.from(expanded);
}

export function fetchCountryOptionsFromJson(
  query: string,
  allLabel = 'Todos',
  includeAllOption = false,
): CountryComboOption[] {
  const allOption: CountryComboOption = {
    value: SHIPPING_ALL_VALUE,
    label: allLabel,
  };
  const withAll = (items: CountryComboOption[]) =>
    includeAllOption ? [allOption, ...items] : items;

  if (!query.trim()) {
    return withAll([
      ...availableRegionOptions.map(toRegionComboOption),
      ...availableCountries.map(toComboOption),
    ]);
  }

  const q = query
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

  const filteredRegions = availableRegionOptions.filter((region) =>
    region.searchValue.includes(q),
  );
  const filteredCountries = availableCountries.filter((country) =>
    country.searchValue.includes(q),
  );

  return withAll([
    ...filteredRegions.map(toRegionComboOption),
    ...filteredCountries.map(toComboOption),
  ]);
}

export function getAllCountryAlpha2Codes(): string[] {
  return availableCountries.map((country) => country.alpha2);
}

export function getCurrenciesForCountryCodes(countryCodes: string[]): string[] {
  const currencies = new Set<string>();

  for (const code of countryCodes) {
    if (code === SHIPPING_ALL_VALUE) continue;

    const upper = String(code || '').toUpperCase().trim();
    const region = regionByCode.get(upper);

    if (region) {
      (region.currencies ?? []).forEach((currency) =>
        currencies.add(currency.toUpperCase()),
      );
      continue;
    }

    const alpha2 = normalizeCountryToAlpha2(code);
    const countryCurrencies = alpha2ToCurrenciesMap.get(alpha2) ?? [];
    countryCurrencies.forEach((currency) => currencies.add(currency));
  }

  return Array.from(currencies).sort();
}

export function getCountryLabelByAlpha2(alpha2: string): string {
  const upper = alpha2.toUpperCase();
  const region = regionByCode.get(upper);
  if (region) return region.name;

  const found = availableCountries.find((c) => c.alpha2 === upper);
  return found?.label ?? alpha2;
}

export function formatCountriesForDisplay(
  countries: Array<string | typeof SHIPPING_ALL_VALUE>,
  allLabel = 'Todos',
): string {
  if (countries.includes(SHIPPING_ALL_VALUE)) return allLabel;

  return countries
    .map((country) =>
      getCountryLabelByAlpha2(normalizeCountryToAlpha2(String(country))),
    )
    .join(', ');
}
